import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Stack,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Chip,
    Tooltip,
    Divider,
    Typography,
    Avatar,
    useMediaQuery,
} from "@mui/material";
import { parseISO, isValid as isValidDate } from "date-fns";
import moment from "moment-timezone";
import { useTheme } from "@emotion/react";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WindowSummary from "./WindowSummary";
import WindowWorkOrderDialog from "../RFWindows/WindowWorkOrderDialog";

import RFWindowDataService from "../../../services/RFWindow.services";

// Helper function to convert decimal to fraction
function decimalToFraction(decimal) {
    if (decimal === null || decimal === undefined || decimal === 0) {
        return "0";
    }

    // Handle negative numbers
    const isNegative = decimal < 0;
    decimal = Math.abs(decimal);

    // If it's already a whole number, return as is
    if (decimal % 1 === 0) {
        return isNegative ? `-${decimal}` : `${decimal}`;
    }

    // Common fractions for typical measurements
    const commonFractions = [
        { decimal: 0.125, fraction: "1/8" },
        { decimal: 0.1875, fraction: "3/16" },
        { decimal: 0.25, fraction: "1/4" },
        { decimal: 0.375, fraction: "3/8" },
        { decimal: 0.5, fraction: "1/2" },
        { decimal: 0.625, fraction: "5/8" },
        { decimal: 0.75, fraction: "3/4" },
        { decimal: 0.875, fraction: "7/8" },
    ];

    // Check for exact matches with common fractions
    const exactMatch = commonFractions.find(
        (f) => Math.abs(f.decimal - decimal) < 0.001
    );
    if (exactMatch) {
        return isNegative ? `-${exactMatch.fraction}` : exactMatch.fraction;
    }

    // Check for mixed numbers (whole number + fraction)
    const wholeNumber = Math.floor(decimal);
    const fractionalPart = decimal - wholeNumber;

    if (wholeNumber > 0) {
        const fractionMatch = commonFractions.find(
            (f) => Math.abs(f.decimal - fractionalPart) < 0.001
        );
        if (fractionMatch) {
            const result = `${wholeNumber} ${fractionMatch.fraction}`;
            return isNegative ? `-${result}` : result;
        }
    }

    // If no common fraction found, use a more general approach
    // Convert to fraction with reasonable denominator
    const tolerance = 0.001;
    for (let denominator = 2; denominator <= 64; denominator++) {
        const numerator = Math.round(decimal * denominator);
        if (Math.abs(decimal - numerator / denominator) < tolerance) {
            // Simplify the fraction
            const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
            const commonDivisor = gcd(numerator, denominator);
            const simplifiedNumerator = numerator / commonDivisor;
            const simplifiedDenominator = denominator / commonDivisor;

            if (simplifiedDenominator === 1) {
                return isNegative
                    ? `-${simplifiedNumerator}`
                    : `${simplifiedNumerator}`;
            }

            const result = `${simplifiedNumerator}/${simplifiedDenominator}`;
            return isNegative ? `-${result}` : result;
        }
    }

    // If all else fails, return the decimal rounded to 3 places
    return isNegative ? `-${decimal.toFixed(3)}` : decimal.toFixed(3);
}

// Temporary normalization function to convert API data to expected format
function normalizeRFWindows(data) {
    const toNumber = (val) => {
        if (val === null || val === undefined || val === "") return null;
        const parsed = Number(val);
        return Number.isNaN(parsed) ? null : parsed;
    };

    return data.map((w, idx) => ({
        id: w.id || idx + 1,
        work_order_number: w.work_order_number || "",
        count: w.count || 0,
        production_schedule: w.production_schedule || null,

        // Project hierarchy - exactly one of these should be set
        project: w.project || null,
        service: w.service || null,
        hse: w.hse || null,

        // Assignment
        assignee: w.assignee || null,
        created_by: w.created_by || null,
        updated_by: w.updated_by || null,

        // Lifecycle dates
        due: w.due || null,
        is_complete: !!w.is_complete,
        completed: w.completed || null,
        created: w.created || null,
        updated: w.updated || null,
        checked: w.checked || null,
        checked_by: w.checked_by || null,
        checked_at: w.checked || null,
        is_archived: !!w.is_archived,
        archived: w.archived || null,
        archived_by: w.archived_by || null,

        // Core window specifications
        quantity: toNumber(w.quantity) || 1,
        clear_width: toNumber(w.clear_width),
        clear_height: toNumber(w.clear_height),
        glass_width: toNumber(w.glass_width),
        glass_height: toNumber(w.glass_height),
        frame_width: toNumber(w.frame_width),
        glass_type: w.glass_type || "",
        glass_thickness: toNumber(w.glass_thickness),
        glass_both_sides: !!w.glass_both_sides,

        // Attachments
        qr_code: w.qr_code || null,
        drawings: Array.isArray(w.drawings) ? w.drawings : [],

        // Notes
        comments: w.comments || "",
    }));
}

// Simple avatar helper for initials
function stringAvatar(name = "") {
    const trimmed = name.trim();
    const first = trimmed.split(" ")[0] || "";
    const initial = first.charAt(0) || "";
    return {
        children: initial,
        sx: {
            bgcolor: "primary.light",
            color: "primary.contrastText",
        },
    };
}

// Due Date / Completion Chip Component
function DueDateChip({ value, completed, completedDate, checkedBy }) {
    const theme = useTheme();

    const currentDate = useMemo(
        () => moment.tz(new Date(), "America/New_York").toDate(),
        []
    );

    // If completed, show completion information
    if (completed) {
        const completedParsed = completedDate ? parseISO(completedDate) : null;

        return (
            <Box
                sx={{
                    width: 120,
                    height: 48,
                    border: 1,
                    borderColor: "success.main",
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.75,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                }}
            >
                <CheckCircleIcon
                    sx={{
                        fontSize: "1.2rem",
                        mr: 1,
                        color: "success.main",
                        flexShrink: 0,
                    }}
                />
                <Stack
                    spacing={0.2}
                    alignItems="flex-start"
                    sx={{ width: "100%", minHeight: 0 }}
                    justifyContent="center"
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            lineHeight: 1.2,
                            color: "success.main",
                        }}
                    >
                        Completed
                    </Typography>
                    <Divider sx={{ width: "100%" }} />
                    <Typography
                        variant="caption"
                        sx={{
                            color: "text.secondary",
                            fontWeight: 400,
                            fontSize: "0.65rem",
                            lineHeight: 1.1,
                        }}
                    >
                        {completedParsed && isValidDate(completedParsed)
                            ? moment(completedParsed).format("MM/DD/YYYY")
                            : "—"}
                    </Typography>
                </Stack>
            </Box>
        );
    }

    // Original due date logic for incomplete items
    const parsed = value ? parseISO(value) : null;
    const delta =
        parsed && isValidDate(parsed)
            ? Math.ceil(
                  (parsed.getTime() - currentDate.getTime()) /
                      (1000 * 3600 * 24)
              )
            : null;

    let dueMessage = "No Due Date";
    if (delta !== null) {
        if (delta === 0) dueMessage = "Today";
        else if (delta === 1) dueMessage = "Tomorrow";
        else if (delta === -1) dueMessage = "Yesterday";
        else if (delta > 1) dueMessage = `In ${delta} days`;
        else dueMessage = `${Math.abs(delta)} days ago`;
    }

    return (
        <Box
            sx={{
                width: 120,
                height: 48,
                border: 1,
                borderColor:
                    delta !== null && delta < 0
                        ? "warning.main"
                        : "primary.main",
                borderRadius: 1,
                px: 1.5,
                py: 0.75,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
            }}
        >
            <EventAvailableIcon
                sx={{
                    fontSize: "1.2rem",
                    mr: 1,
                    color:
                        delta !== null && delta < 0
                            ? "warning.main"
                            : "primary.main",
                    flexShrink: 0,
                }}
            />
            <Stack
                spacing={0.2}
                alignItems="flex-start"
                sx={{ width: "100%", minHeight: 0 }}
                justifyContent="center"
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        lineHeight: 1.2,
                        color:
                            delta !== null && delta < 0
                                ? "warning.main"
                                : "primary.main",
                    }}
                >
                    {dueMessage}
                </Typography>
                <Divider sx={{ width: "100%" }} />
                <Typography
                    variant="caption"
                    sx={{
                        color: "text.secondary",
                        fontWeight: 400,
                        fontSize: "0.65rem",
                        lineHeight: 1.1,
                    }}
                >
                    {parsed && isValidDate(parsed)
                        ? moment(parsed).format("MM/DD/YYYY")
                        : "—"}
                </Typography>
            </Stack>
        </Box>
    );
}

// Window Order List Component
function WindowOrderList({ handleOpenSnackbar, darkState, user, token }) {
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [openWorkOrder, setOpenWorkOrder] = useState(false);

    useEffect(() => {
        if (!token) {
            setItems([]);
            return;
        }

        setIsLoading(true);
        RFWindowDataService.getAllRFWindows(token)
            .then((response) => {
                const apiData = Array.isArray(response.data)
                    ? response.data
                    : [];
                setItems(normalizeRFWindows(apiData));
            })
            .catch((error) => {
                console.error("Failed to load RF windows", error);
                setItems([]);
                if (handleOpenSnackbar) {
                    handleOpenSnackbar(
                        "error",
                        "Unable to load RF windows right now."
                    );
                }
            })
            .finally(() => setIsLoading(false));
    }, [token, handleOpenSnackbar]);

    const visibleItems = items.filter((w) => !w.is_archived);
    const incompleteItems = visibleItems.filter((w) => !w.is_complete);

    const totalQty = useMemo(
        () =>
            visibleItems.reduce((sum, w) => sum + (Number(w.quantity) || 0), 0),
        [visibleItems]
    );

    const incompleteQty = useMemo(
        () =>
            incompleteItems.reduce(
                (sum, w) => sum + (Number(w.quantity) || 0),
                0
            ),
        [incompleteItems]
    );

    // Calculate area by glass type for all visible items
    const areaByGlassType = useMemo(() => {
        const breakdown = {};

        visibleItems.forEach((w) => {
            const glassKey = w.glass_type || "Unknown";

            if (!breakdown[glassKey]) {
                breakdown[glassKey] = {
                    label: glassKey,
                    totalArea: 0,
                    openArea: 0,
                    totalQty: 0,
                    openQty: 0,
                    totalOrders: 0,
                    openOrders: 0,
                };
            }

            // Calculate area for this window (glass area)
            const glassArea =
                w.glass_width && w.glass_height
                    ? w.glass_width * w.glass_height * (w.quantity || 1)
                    : 0;

            breakdown[glassKey].totalArea += glassArea;
            breakdown[glassKey].totalQty += w.quantity || 0;
            breakdown[glassKey].totalOrders += 1;

            if (!w.is_complete) {
                breakdown[glassKey].openArea += glassArea;
                breakdown[glassKey].openQty += w.quantity || 0;
                breakdown[glassKey].openOrders += 1;
            }
        });

        return breakdown;
    }, [visibleItems]);

    const incompleteCount = incompleteItems.length;
    const totalCount = visibleItems.length;

    const openWO = (order) => {
        setSelected(order);
        setOpenWorkOrder(true);
    };
    const closeWO = () => {
        setOpenWorkOrder(false);
        setSelected(null);
    };

    const replaceAndSort = (updatedItem) =>
        setItems((prev) => {
            const withUpdate = prev.map((w) =>
                w.id === updatedItem.id ? updatedItem : w
            );
            return [...withUpdate].sort(
                (a, b) => Number(a.is_complete) - Number(b.is_complete)
            );
        });

    const updateDue = async (orderId, iso) => {
        if (!token) {
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "error",
                    "No token available to update RF window."
                );
            return;
        }
        try {
            const response = await RFWindowDataService.updateDueDate(
                orderId,
                { due: iso },
                token
            );
            const updated =
                response && response.data
                    ? normalizeRFWindows([response.data])[0]
                    : null;
            if (updated) {
                replaceAndSort(updated);
            }
            handleOpenSnackbar &&
                handleOpenSnackbar("success", "Due date updated");
        } catch (error) {
            console.error("Failed to update due date", error);
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "error",
                    "Unable to update due date right now."
                );
        }
    };

    const markChecked = async (orderId) => {
        if (!token) {
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "error",
                    "No token available to update RF window."
                );
            return;
        }
        try {
            const response = await RFWindowDataService.toggleChecked(
                orderId,
                token
            );
            const updated =
                response && response.data
                    ? normalizeRFWindows([response.data])[0]
                    : null;
            if (updated) {
                replaceAndSort(updated);
            }
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "success",
                    "RF window order marked as checked and completed"
                );
        } catch (error) {
            console.error("Failed to mark RF window checked", error);
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "error",
                    "Unable to update RF window right now."
                );
        } finally {
            closeWO();
        }
    };

    const markUnchecked = async (orderId) => {
        if (!token) {
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "error",
                    "No token available to update RF window."
                );
            return;
        }
        try {
            const response = await RFWindowDataService.toggleChecked(
                orderId,
                token
            );
            const updated =
                response && response.data
                    ? normalizeRFWindows([response.data])[0]
                    : null;
            if (updated) {
                replaceAndSort(updated);
            }
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "info",
                    "RF window order unchecked and marked as incomplete"
                );
        } catch (error) {
            console.error("Failed to uncheck RF window", error);
            handleOpenSnackbar &&
                handleOpenSnackbar(
                    "error",
                    "Unable to update RF window right now."
                );
        } finally {
            closeWO();
        }
    };

    return (
        <Box
            sx={{
                m: { xs: 1, sm: 2, md: 4 },
                mt: 0,
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <WindowSummary
                    darkState={darkState}
                    totalCount={totalCount}
                    openCount={incompleteCount}
                    totalQty={totalQty}
                    openQty={incompleteQty}
                    glassTypeBreakdown={areaByGlassType}
                    rawWindowData={visibleItems}
                />
            </Box>

            {isLoading && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mb: 2 }}
                >
                    Loading RF windows...
                </Typography>
            )}

            {!isLoading && visibleItems.length === 0 && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mb: 2 }}
                >
                    No RF windows to display.
                </Typography>
            )}

            {visibleItems.length > 0 && (
                <List
                    sx={{
                        width: "100%",
                        bgcolor: "background.paper",
                        border: darkState ? "1.5px solid" : "3px solid",
                        borderRadius: 2,
                        pt: 0,
                        pb: 0,
                        borderColor: "primary.main",
                        overflowX: "hidden",
                    }}
                >
                    {visibleItems.map((order, i) => {
                        const assigneeName = order.assignee
                            ? `${order.assignee.first_name || ""} ${order
                                  .assignee.last_name || ""}`.trim()
                            : "";

                        // Determine which project target is being used
                        const projectLike =
                            order.project || order.service || order.hse || {};
                        const targetType = order.project
                            ? "Project"
                            : order.service
                            ? "Service"
                            : order.hse
                            ? "HSE"
                            : "";

                        // Format dimensions
                        const clearDimensions =
                            order.clear_width && order.clear_height
                                ? `${decimalToFraction(
                                      order.clear_width
                                  )}" × ${decimalToFraction(
                                      order.clear_height
                                  )}"`
                                : "—";

                        const glassDimensions =
                            order.glass_width && order.glass_height
                                ? `${decimalToFraction(
                                      order.glass_width
                                  )}" × ${decimalToFraction(
                                      order.glass_height
                                  )}"`
                                : "—";

                        const dueChip = (
                            <DueDateChip
                                value={order.due}
                                completed={order.is_complete}
                                completedDate={order.completed}
                                checkedBy={order.checked_by}
                            />
                        );

                        return (
                            <div key={order.id}>
                                <ListItem
                                    disablePadding
                                    secondaryAction={
                                        !isMobile && (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                sx={{ pr: 1 }}
                                            >
                                                {dueChip}
                                            </Stack>
                                        )
                                    }
                                >
                                    <ListItemButton
                                        onClick={() => openWO(order)}
                                        sx={{
                                            position: "relative",
                                            overflow: "hidden",
                                            minWidth: 0,
                                            opacity: order.is_complete
                                                ? 0.45
                                                : 1,
                                            bgcolor: order.is_complete
                                                ? "action.hover"
                                                : "background.paper",
                                            borderLeft: order.is_complete
                                                ? 4
                                                : undefined,
                                            borderLeftColor: order.is_complete
                                                ? "success.light"
                                                : undefined,
                                        }}
                                    >
                                        {order.is_complete && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 0,
                                                    bottom: 0,
                                                    left: 0,
                                                    width: 4,
                                                    bgcolor: "success.main",
                                                    zIndex: 3,
                                                }}
                                            />
                                        )}
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: 4,
                                                left: 6,
                                                width: 10,
                                                height: 10,
                                                borderRadius: "50%",
                                                bgcolor: order.is_complete
                                                    ? "success.main"
                                                    : "warning.main",
                                                zIndex: 2,
                                            }}
                                        />

                                        <ListItemText
                                            primaryTypographyProps={{
                                                component: "div",
                                                variant: "subtitle1",
                                                sx: {
                                                    fontWeight: 700,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                },
                                            }}
                                            primary={
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    sx={{ overflow: "hidden" }}
                                                >
                                                    <Chip
                                                        size="small"
                                                        variant="filled"
                                                        color="primary"
                                                        label={
                                                            projectLike.number ||
                                                            "—"
                                                        }
                                                        sx={{ maxWidth: 120 }}
                                                    />
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight={700}
                                                        noWrap
                                                        sx={{ maxWidth: "60%" }}
                                                    >
                                                        {projectLike.name ||
                                                            projectLike.number ||
                                                            order.work_order_number}
                                                    </Typography>
                                                </Stack>
                                            }
                                            secondaryTypographyProps={{
                                                component: "div",
                                            }}
                                            secondary={
                                                <>
                                                    <Stack
                                                        spacing={0.25}
                                                        sx={{
                                                            wordBreak:
                                                                "break-word",
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            color="text.primary"
                                                        >
                                                            WO#:{" "}
                                                            {order.work_order_number ||
                                                                "—"}
                                                        </Typography>
                                                        {targetType && (
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                Type:{" "}
                                                                {targetType}
                                                            </Typography>
                                                        )}
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Quantity:{" "}
                                                            {order.quantity ||
                                                                0}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Clear Opening:{" "}
                                                            {clearDimensions}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Glass:{" "}
                                                            {glassDimensions}
                                                        </Typography>
                                                        {order.glass_type && (
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                            >
                                                                Glass Type:{" "}
                                                                {
                                                                    order.glass_type
                                                                }
                                                            </Typography>
                                                        )}
                                                        {assigneeName ? (
                                                            <Tooltip
                                                                title={`Assigned to ${assigneeName}`}
                                                                disableFocusListener
                                                                disableTouchListener
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        mt: 3,
                                                                        px: 1,
                                                                        py: 0.75,
                                                                        width:
                                                                            "fit-content",
                                                                        alignSelf:
                                                                            "flex-start",
                                                                        border: 1,
                                                                        borderColor:
                                                                            "divider",
                                                                        borderRadius: 1,
                                                                        bgcolor:
                                                                            "background.default",
                                                                        display:
                                                                            "inline-flex",
                                                                        alignItems:
                                                                            "center",
                                                                    }}
                                                                >
                                                                    <Stack
                                                                        direction="row"
                                                                        spacing={
                                                                            0.75
                                                                        }
                                                                        alignItems="center"
                                                                    >
                                                                        {(() => {
                                                                            const avatarProps = stringAvatar(
                                                                                assigneeName
                                                                            );
                                                                            return (
                                                                                <Avatar
                                                                                    {...avatarProps}
                                                                                    sx={{
                                                                                        ...avatarProps.sx,
                                                                                        width: 28,
                                                                                        height: 28,
                                                                                        fontSize: 12,
                                                                                    }}
                                                                                />
                                                                            );
                                                                        })()}
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="text.secondary"
                                                                            noWrap
                                                                            sx={{
                                                                                fontWeight: 600,
                                                                            }}
                                                                        >
                                                                            {
                                                                                assigneeName
                                                                            }
                                                                        </Typography>
                                                                    </Stack>
                                                                </Box>
                                                            </Tooltip>
                                                        ) : null}
                                                    </Stack>
                                                    {isMobile && (
                                                        <Box sx={{ mt: 2 }}>
                                                            {dueChip}
                                                        </Box>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>

                                {i < visibleItems.length - 1 && (
                                    <Divider
                                        sx={{ borderColor: "primary.main" }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </List>
            )}

            <WindowWorkOrderDialog
                open={openWorkOrder}
                onClose={closeWO}
                order={selected}
                user={user}
                onMarkChecked={markChecked}
                onMarkUnchecked={markUnchecked}
            />
        </Box>
    );
}

export default WindowOrderList;
